import React from 'react';
import { useTranslation } from 'react-i18next';
import { ORDER_STATUS } from 'src/constants/common';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';

import checkCircle from '../../assets/icons/filled-icons/check-circle.svg';

import { setTrackParams, trackCount as trackCountSelector, trackOverviewLoading, trackParams as trackParamsSelector } from './track.slice';

const CountValue = ({ isLoading, value }: { isLoading: boolean; value: number }) => (
    <span className={`text-[12px] leading-[20px] font-semibold text-textSecondary ${isLoading ? 'custom-loading w-6 h-5 rounded' : ''}`}>{!isLoading ? value : ''}</span>
);

const SubCountValue = ({ isLoading, value, isActive }: { isLoading: boolean; value: number; isActive: boolean }) => (
    <p className={`text-[12px] leading-[20px] font-normal ${isActive ? 'text-textSecondary font-semibold' : 'text-textSecondary'} ${isLoading ? 'custom-loading w-6 h-5 rounded' : ''}`}>
        {!isLoading ? value : ''}
    </p>
);

const TrackOverviewStats = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const trackParams = useAppSelector(trackParamsSelector);
    const trackCount = useAppSelector(trackCountSelector);
    const isLoading = useAppSelector(trackOverviewLoading);
    const activeTab = trackParams.status;
    const filterBySubStatus = trackParams.filterBySubStatus ?? '';

    const handleTabSwitch = (tabValue: string) => {
        if (activeTab === tabValue && !filterBySubStatus) return;
        dispatch(setTrackParams({ status: tabValue, filterBySubStatus: '' }));
    };

    const handleSubStatusClick = (e: React.MouseEvent, parentTab: string, subStatus: string) => {
        e.stopPropagation();
        const isCurrentlySelected = activeTab === parentTab && filterBySubStatus === subStatus;
        const nextSubStatus = isCurrentlySelected ? '' : subStatus;
        dispatch(setTrackParams({ status: parentTab, filterBySubStatus: nextSubStatus }));
    };

    const subStatusItemClass = (parentTab: string, subStatus: string) =>
        `cursor-pointer rounded-md px-1.5 py-1 transition-colors ${activeTab === parentTab && filterBySubStatus === subStatus ? 'bg-[#E9EDF1]' : 'hover:bg-gray100'}`;

    const subStatusLabelClass = (parentTab: string, subStatus: string) =>
        `text-[12px] leading-[18px] font-normal ${activeTab === parentTab && filterBySubStatus === subStatus ? 'text-textSecondary font-semibold' : 'text-textSecondary'}`;

    const isSubActive = (parentTab: string, subStatus: string) => activeTab === parentTab && filterBySubStatus === subStatus;

    return (
        <div className="flex flex-row gap-2">
            {/* Pickups column */}
            <div
                className={`px-3 relative py-3 w-full cursor-pointer transition-colors ${activeTab === ORDER_STATUS.DISPATCHED ? 'bg-gray50 rounded-lg' : 'hover:bg-gray50'}`}
                onClick={() => handleTabSwitch(ORDER_STATUS.DISPATCHED)}
            >
                <div className="flex flex-col mb-1">
                    <span className="text-xs leading-[18px] text-textSecondary font-medium">{t('Radar.Pickups')}</span>
                    <CountValue isLoading={isLoading} value={trackCount.pickups.total} />
                    {activeTab === ORDER_STATUS.DISPATCHED && <img src={checkCircle} alt="check" className="w-3.5 h-3.5 absolute top-1.5 right-1.5" />}
                </div>
                <div className="thisSectionAccordion">
                    <div className="overflow-hidden">
                        <div className="flex flex-col gap-y-1 border-t border-utilityGray200 pt-1">
                            <div className={subStatusItemClass(ORDER_STATUS.DISPATCHED, 'completed')} onClick={(e) => handleSubStatusClick(e, ORDER_STATUS.DISPATCHED, 'completed')}>
                                <p className={subStatusLabelClass(ORDER_STATUS.DISPATCHED, 'completed')}>{t('Radar.Completed')}</p>
                                <SubCountValue isLoading={isLoading} value={trackCount.pickups.completed} isActive={isSubActive(ORDER_STATUS.DISPATCHED, 'completed')} />
                            </div>
                            <div className={subStatusItemClass(ORDER_STATUS.DISPATCHED, 'pending')} onClick={(e) => handleSubStatusClick(e, ORDER_STATUS.DISPATCHED, 'pending')}>
                                <p className={subStatusLabelClass(ORDER_STATUS.DISPATCHED, 'pending')}>{t('Radar.Pending')}</p>
                                <SubCountValue isLoading={isLoading} value={trackCount.pickups.pending} isActive={isSubActive(ORDER_STATUS.DISPATCHED, 'pending')} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="relative before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-px before:bg-[linear-gradient(180deg,#E3E3E3_35.1%,rgba(255,255,255,0.00)_100%)]"></div>

            {/* In Transit column */}
            <div
                className={`px-3 relative py-3 w-full cursor-pointer transition-colors ${activeTab === ORDER_STATUS.IN_TRANSIT ? 'bg-gray50 rounded-lg' : 'hover:bg-gray50'}`}
                onClick={() => handleTabSwitch(ORDER_STATUS.IN_TRANSIT)}
            >
                <div className="flex flex-col mb-1">
                    <span className="text-xs leading-[18px] text-textSecondary font-medium">{t('Radar.In_Transit')}</span>
                    <CountValue isLoading={isLoading} value={trackCount.inTransit.total} />
                    {activeTab === ORDER_STATUS.IN_TRANSIT && <img src={checkCircle} alt="check" className="w-4 h-4 absolute top-1.5 right-1.5" />}
                </div>
                <div className="thisSectionAccordion">
                    <div className="overflow-hidden">
                        <div className="flex flex-col gap-y-1 border-t border-utilityGray200 pt-1">
                            <div className={subStatusItemClass(ORDER_STATUS.IN_TRANSIT, 'on_track')} onClick={(e) => handleSubStatusClick(e, ORDER_STATUS.IN_TRANSIT, 'on_track')}>
                                <p className={subStatusLabelClass(ORDER_STATUS.IN_TRANSIT, 'on_track')}>{t('Radar.On_Track')}</p>
                                <SubCountValue isLoading={isLoading} value={trackCount.inTransit.onTrack} isActive={isSubActive(ORDER_STATUS.IN_TRANSIT, 'on_track')} />
                            </div>
                            <div className={subStatusItemClass(ORDER_STATUS.IN_TRANSIT, 'delayed')} onClick={(e) => handleSubStatusClick(e, ORDER_STATUS.IN_TRANSIT, 'delayed')}>
                                <p className={subStatusLabelClass(ORDER_STATUS.IN_TRANSIT, 'delayed')}>{t('Radar.Delayed')}</p>
                                <SubCountValue isLoading={isLoading} value={trackCount.inTransit.delayed} isActive={isSubActive(ORDER_STATUS.IN_TRANSIT, 'delayed')} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="relative before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-px before:bg-[linear-gradient(180deg,#E3E3E3_35.1%,rgba(255,255,255,0.00)_100%)]"></div>

            {/* Deliveries column */}
            <div
                className={`px-3 relative py-3 w-full cursor-pointer transition-colors ${activeTab === ORDER_STATUS.DELIVERED ? 'bg-gray50 rounded-lg' : 'hover:bg-gray50'}`}
                onClick={() => handleTabSwitch(ORDER_STATUS.DELIVERED)}
            >
                <div className="flex flex-col mb-1">
                    <span className="text-xs leading-[18px] text-textSecondary font-medium">{t('Radar.Deliveries')}</span>
                    <CountValue isLoading={isLoading} value={trackCount.deliveries.total} />
                    {activeTab === ORDER_STATUS.DELIVERED && <img src={checkCircle} alt="check" className="w-4 h-4 absolute top-1.5 right-1.5" />}
                </div>
                <div className="thisSectionAccordion">
                    <div className="overflow-hidden">
                        <div className="flex flex-col gap-y-1 border-t border-utilityGray200 pt-1">
                            <div className={subStatusItemClass(ORDER_STATUS.DELIVERED, 'completed')} onClick={(e) => handleSubStatusClick(e, ORDER_STATUS.DELIVERED, 'completed')}>
                                <p className={subStatusLabelClass(ORDER_STATUS.DELIVERED, 'completed')}>{t('Radar.Completed')}</p>
                                <SubCountValue isLoading={isLoading} value={trackCount.deliveries.completed ?? 0} isActive={isSubActive(ORDER_STATUS.DELIVERED, 'completed')} />
                            </div>
                            <div className={subStatusItemClass(ORDER_STATUS.DELIVERED, 'pending')} onClick={(e) => handleSubStatusClick(e, ORDER_STATUS.DELIVERED, 'pending')}>
                                <p className={subStatusLabelClass(ORDER_STATUS.DELIVERED, 'pending')}>{t('Radar.Pending')}</p>
                                <SubCountValue isLoading={isLoading} value={trackCount.deliveries.pending} isActive={isSubActive(ORDER_STATUS.DELIVERED, 'pending')} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrackOverviewStats;
